import { sendEmailSES } from '../utils/emailConfig';
import { format } from 'date-fns';
import { htmlToText } from 'html-to-text';
import unifiedEmailService from './unifiedEmailService';

console.log('🚀 Loading UPDATED emailTemplateService.js - ' + new Date().toISOString());

// Helper function to format phone numbers
function formatPhoneNumber(phone) {
  // Remove all non-digits
  const cleaned = ('' + phone).replace(/\D/g, '');
  
  // Remove leading 1 if present
  const number = cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
  
  // Format the phone number
  const areaCode = number.slice(0, 3);
  const prefix = number.slice(3, 6);
  const line = number.slice(6, 10);

  return {
    unformattedLong: cleaned,
    unformattedShort: number,
    formattedLong: `+1 (${areaCode}) ${prefix}-${line}`,
    formattedShort: `(${areaCode}) ${prefix}-${line}`
  };
}

function toE164(phone) {
  // Remove all non-digits
  const cleaned = ('' + phone).replace(/\D/g, '');
  // If it starts with 1, remove it
  const withoutCountry = cleaned.startsWith('1') ? cleaned.substring(1) : cleaned;
  // If we have 10 digits, format as E.164
  return withoutCountry.length === 10 ? `+1${withoutCountry}` : phone;
}

export const emailTemplateService = {
  async loadTemplates(supabase) {
    try {
      const { data: templates, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return templates;
    } catch (error) {
      console.error('Error loading templates:', error);
      throw error;
    }
  },

  async saveTemplate(supabase, template) {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .upsert({
          id: template.id,
          name: template.name,
          subject: template.subject,
          html_content: template.html_content || template.htmlContent,
          text_content: template.text_content || template.textContent,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving template:', error);
      throw error;
    }
  },

  async deleteTemplate(supabase, templateId) {
    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  },

  /**
   * Send a test email using a template
   * @param {Object} params - Email parameters
   * @param {Object} params.template - Email template object
   * @param {string} params.testName - Test recipient name
   * @param {string} params.testPhone - Test recipient phone number
   * @param {string} params.clubName - Club name
   * @param {Object} params.supabase - Supabase client instance
   */
  async sendTestEmail({ template, testName, testPhone, clubName, supabase }) {
    try {
      console.log('Test email input:', { template, testName, clubName, testPhone });

      // Split name into first and last
      const [firstName, ...lastNameParts] = testName.split(' ');
      const lastName = lastNameParts.join(' ');

      // Process phone number
      const phoneResult = toE164(testPhone);
      console.log('Test email phone number:', {
        input: testPhone,
        converted: phoneResult,
        isE164: phoneResult?.startsWith('+1')
      });

      // Prepare variables
      const variables = {
        firstName,
        lastName,
        fullName: testName,
        phone: phoneResult,
        clubName,
        sendDate: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      };

      console.log('Test email variables:', variables);

      // Prepare email parameters
      const emailParams = {
        to: template.test_email,
        subject: template.subject,
        html: template.html_content,
        text: template.text_content,
        variables,
        template_id: template.id,
        supabase
      };

      console.log('Sending test email with params:', emailParams);

      // Send the email
      const result = await unifiedEmailService.sendEmail(emailParams);

      return { success: true, messageId: result.messageId };
    } catch (error) {
      console.error('Error sending test email:', error);
      throw error;
    }
  }
};
